import { Flex, GlobalToken } from "antd";
import { positionToXY } from "helpers/utils";
import { useAntTheme } from "hooks/useAntTheme";
import { DocumentExtract } from "pages/media/types";
import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  Highlight,
  IHighlight,
  NewHighlight,
  PdfHighlighter,
  PdfLoader,
  Popup,
  Tip,
} from "react-pdf-highlighter";
import "react-pdf-highlighter/dist/style.css";
import styled from "styled-components";
import { PDFJS_WORKER_SRC } from "utils/pdfjsConfig";
import {
  CitationBadge,
  HighlightPopup,
  CustomSpinner,
} from "@libertify/ui";

export const PdfViewer = forwardRef(function PdfViewer(
  {
    highlights = [],
    url,
    withTip = false,
    onHighlightClick,
    activeHighlightId,
    scrollToHighlightYOffset = 0,
  }: {
    highlights?: IHighlight[];
    url: string;
    withTip?: boolean;
    onHighlightClick?: (sourceId: string) => void;
    activeHighlightId?: string;
    scrollToHighlightYOffset?: number;
  },
  ref: React.Ref<void>
) {
  const getHighlightById = (id: string) => {
    return highlights?.find((highlight) => highlight.id === id);
  };

  const { theme } = useAntTheme();
  const addHighlight = (highlight: NewHighlight) => {
    console.log("Saving highlight", highlight);
  };

  const scrolledPage = useRef(0);
  const scrolledPages = useRef<number[]>([]);
  const scrollViewerTo = useRef<(highlight: IHighlight) => void>(() => {});
  useImperativeHandle(ref, () => {
    return {
      scrollViewerTo(source: DocumentExtract) {
        const highlight = {
          position: {
            pageNumber: source.occurrences[0].page_number + 1,
            usePdfCoordinates: true,
            boundingRect: {
              ...positionToXY(source.occurrences[0].coordinates),
              width: 1000,
              height: 300,
              pageNumber: source.occurrences[0].page_number + 1,
            },
            rects: [],
          },
        };

        scrolledPage.current = highlight.position.pageNumber;
        scrolledPages.current.push(highlight.position.pageNumber);

        scrollViewerTo.current({
          ...highlight,
          position: {
            ...highlight.position,
            boundingRect: {
              ...highlight.position.boundingRect,
              y1: highlight.position.boundingRect.y1 + scrollToHighlightYOffset,
            },
          },
        } as never);
      },
    };
  }, [scrollViewerTo, scrollToHighlightYOffset]);

  const parseIdFromHash = () =>
    globalThis.document.location.hash.slice("#highlight-".length);

  const resetHash = () => {
    globalThis.document.location.hash = "";
  };

  const scrollToHighlightFromHash = () => {
    const highlight = getHighlightById(parseIdFromHash());

    if (highlight) {
      scrollViewerTo.current(highlight);
    }
  };

  return (
    <PdfLoader
      url={url}
      beforeLoad={<CustomSpinner />}
      workerSrc={PDFJS_WORKER_SRC}
    >
      {(pdfDocument) => (
        <PdfHighlighter
          pdfDocument={pdfDocument}
          enableAreaSelection={(event) => event.altKey}
          onScrollChange={resetHash}
          scrollRef={(scrollTo) => {
            scrollViewerTo.current = scrollTo;
            scrollToHighlightFromHash();
          }}
          onSelectionFinished={(
            position,
            content,
            hideTipAndSelection,
            transformSelection
          ) => (
            <Tip
              onOpen={transformSelection}
              onConfirm={(comment) => {
                addHighlight({ content, position, comment });
                hideTipAndSelection();
              }}
            />
          )}
          highlightTransform={(
            highlight,
            index,
            setTip,
            hideTip,
            _viewportToScaled,
            _screenshot,
            isScrolledTo
          ) => {
            let highlightIndex = 0;

            if (highlights) {
              highlightIndex = highlights?.findIndex(
                (h) => h.id === highlight.id
              );
            }

            const component = (
              <HighlightWrapper
                theme={theme} // not working if not passing the theme again
                className={
                  activeHighlightId === highlight.id ? "highlight-selected" : ""
                }
              >
                <Highlight
                  onClick={() => {
                    onHighlightClick?.(highlight.id);
                  }}
                  isScrolledTo={isScrolledTo}
                  position={highlight.position}
                  comment={highlight.comment}
                />
                <CitationBadge
                  index={highlightIndex + 1}
                  variant="default"
                  position={{
                    left: highlight?.position.boundingRect.left,
                    top: highlight?.position.boundingRect.top - 15,
                  }}
                />
              </HighlightWrapper>
            );

            return withTip ? (
              <Popup
                popupContent={<HighlightPopup {...highlight} />}
                onMouseOver={(popupContent) =>
                  setTip(highlight, () => popupContent)
                }
                onMouseOut={hideTip}
                key={index}
              >
                {component}
              </Popup>
            ) : (
              component
            );
          }}
          highlights={highlights}
        />
      )}
    </PdfLoader>
  );
});
const HighlightWrapper = styled(Flex)<{ theme: GlobalToken }>`
  position: relative;
  &.highlight-selected .Highlight__part {
    background-color: ${({ theme }) => theme.colorPrimary}66 !important;
  }
`;
